import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const LABELS = {
  name: "Nama", code: "Kode Sparepart", spec: "Spesifikasi", qty_plan: "Jumlah Kebutuhan", unit: "Satuan", supplier: "Supplier",
  plan_price: "Harga Rencana (Rp)", qty_received: "Jumlah Diterima", qty_installed: "Jumlah Terpasang",
  actual_price: "Harga Aktual / satuan (Rp)", actual_total: "Biaya Aktual (Rp)", status: "Status",
};
export const PLAN_FIELDS = ["name", "code", "spec", "qty_plan", "unit", "supplier", "plan_price"];
export const ACTUAL_FIELDS = ["status", "qty_received", "qty_installed", "actual_price", "actual_total"];
const NUM = new Set(["qty_plan", "plan_price", "qty_received", "qty_installed", "actual_price", "actual_total"]);

export const RapItemDialog = ({ item, kind, statuses, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(item ? { ...item } : {}); setReason(""); }, [item]);
  if (!item) return null;
  const fields = (list) => list.filter((f) => item.editable.includes(f) && (kind === "sparepart" || !["code", "qty_received", "qty_installed", "actual_price"].includes(f)));
  const save = async (list) => {
    const changes = {};
    fields(list).forEach((f) => {
      const v = NUM.has(f) ? (form[f] === "" || form[f] == null ? null : Number(form[f])) : form[f];
      if (v !== item[f] && !(v == null && item[f] == null)) changes[f] = v;
    });
    if (!Object.keys(changes).length) { toast.info("Tidak ada perubahan"); return; }
    setBusy(true);
    try { await api.patch(`/rap/items/${item.id}`, { changes, reason }); toast.success("Tersimpan"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const cancelItem = async () => {
    const r = window.prompt("Alasan pembatalan item (wajib). Item diarsipkan, tidak dihapus:") || "";
    if (!r.trim()) return;
    try { await api.post(`/rap/items/${item.id}/cancel`, { reason: r }); toast.success("Item dibatalkan"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const input = (f) => f === "status" ? (
    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
      <SelectTrigger data-testid="rap-edit-status"><SelectValue /></SelectTrigger>
      <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
    </Select>
  ) : (
    <Input type={NUM.has(f) ? "number" : "text"} min="0" value={form[f] ?? ""} data-testid={`rap-edit-${f}`}
      onChange={(e) => setForm({ ...form, [f]: e.target.value })} />
  );
  const section = (title, list, btn, testid) => fields(list).length > 0 && (
    <div className="space-y-2 border rounded-md p-3">
      <div className="text-xs font-semibold text-navy uppercase">{title}</div>
      <div className="grid grid-cols-2 gap-2">{fields(list).map((f) => <div key={f}><label className="text-[11px] text-muted-foreground">{LABELS[f]}</label>{input(f)}</div>)}</div>
      <Button size="sm" disabled={busy} onClick={() => save(list)} className="bg-primary hover:bg-red-700 w-full" data-testid={testid}>{btn}</Button>
    </div>
  );
  return (
    <Dialog open={!!item} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="rap-item-dialog">
        <DialogHeader><DialogTitle>{item.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {section("Rencana", PLAN_FIELDS, "Simpan Rencana", "rap-save-plan")}
          {section("Aktual", ACTUAL_FIELDS, "Simpan Aktual", "rap-save-actual")}
          {fields([...PLAN_FIELDS, ...ACTUAL_FIELDS]).length > 0 && (
            <div><label className="text-[11px] text-muted-foreground">Alasan (wajib bila mengubah harga/aktual yang sudah disetujui/terverifikasi, atau proyek sudah Closed)</label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="rap-edit-reason" /></div>
          )}
          {item.history?.length > 0 && (
            <div className="text-[11px] text-muted-foreground space-y-1 max-h-40 overflow-y-auto" data-testid="rap-item-history">
              {item.history.slice().reverse().map((h, i) => <div key={i}>{new Date(h.at).toLocaleString("id-ID")} · {h.by} ({h.role}) · {h.action}{h.reason ? ` · ${h.reason}` : ""}</div>)}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {fields(PLAN_FIELDS).length > 0 && <Button variant="outline" className="text-primary" disabled={busy} onClick={cancelItem} data-testid="rap-cancel-item">Batalkan Item</Button>}
          <Button variant="outline" onClick={onClose} data-testid="rap-dialog-close">Tutup</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
